<template>
  <div>
    <!-- Callback Partner ID -->
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center gap-field">
          <v-text-field
            id="callbackPartnerId"
            ref="callbackPartnerId"
            outlined
            label="Callback Partner ID *"
            v-model="callbackPartnerId"
            @change="$v.callbackPartnerId.$touch()"
            @blur="$v.callbackPartnerId.$touch()"
            @input="removeSpaces('callbackPartnerId')"
            @keydown.space.prevent
            :error-messages="callbackPartnerIdErrors"
            :maxlength="36"
            :filled="isFromAggregator"
            :readonly="isFromAggregator"
            autocomplete="off"
            class="flex-grow-1"
          />
          <v-btn
            outlined
            color="primary"
            class="ml-2 button-align"
            @click="generateCallbackPartnerId"
            :disabled="isFromAggregator"
          >
            Generate
          </v-btn>
          <v-btn
            outlined
            color="error"
            class="ml-2 button-align"
            @click="resetCallbackPartnerId"
            :disabled="isFromAggregator"
          >
            Reset
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Callback Partner Client Secret -->
    <v-row class="mt-4">
      <v-col cols="12">
        <div class="d-flex align-center gap-field">
          <v-text-field
            id="callbackPartnerClientSecret"
            ref="callbackPartnerClientSecret"
            outlined
            label="Callback Partner Client Secret *"
            v-model="callbackPartnerClientSecret"
            @change="$v.callbackPartnerClientSecret.$touch()"
            @blur="$v.callbackPartnerClientSecret.$touch()"
            @input="removeSpaces('callbackPartnerClientSecret')"
            @keydown.space.prevent
            :error-messages="callbackPartnerClientSecretErrors"
            :maxlength="36"
            :filled="isFromAggregator"
            :readonly="isFromAggregator"
            autocomplete="off"
            class="flex-grow-1"
          />
          <v-btn
            outlined
            color="primary"
            class="ml-2 button-align"
            @click="generateCallbackPartnerClientSecret"
            :disabled="isFromAggregator"
          >
            Generate
          </v-btn>
          <v-btn
            outlined
            color="error"
            class="ml-2 button-align"
            @click="resetCallbackPartnerClientSecret"
            :disabled="isFromAggregator"
          >
            Reset
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate';
import {  maxLength, alphaNum } from 'vuelidate/lib/validators';

export default {
  name: 'CallbackPartnerField',
  mixins: [validationMixin],

  props: {
    partnerId: {
      type: String,
      default: ''
    },
    partnerClientSecret: {
      type: String,
      default: ''
    },
    isFromAggregator: {
      type: Boolean,
      default: false
    },
    externalPartnerIdErrors: {
      type: Array,
      default: () => []
    },
    externalClientSecretErrors: {
      type: Array,
      default: () => []
    }
  },

  validations: {
    callbackPartnerId: {
      required() {
        return !!this.callbackPartnerId;
      },
      maxLength: maxLength(36),
      noSpace() {
        return !this.callbackPartnerId.includes(' ');
      },
      alphaNum
    },
    callbackPartnerClientSecret: {
      required() {
        return !!this.callbackPartnerClientSecret;
      },
      maxLength: maxLength(36),
      noSpace() {
        return !this.callbackPartnerClientSecret.includes(' ');
      },
      alphaNum
    }
  },

  data() {
    return {
      callbackPartnerId: '',
      callbackPartnerClientSecret: ''
    };
  },

  computed: {
    callbackPartnerIdErrors() {
      const errors = [];
      if (!this.$v.callbackPartnerId.$dirty) return [...errors, ...this.externalPartnerIdErrors];
      if (!this.$v.callbackPartnerId.required) errors.push('Callback Partner ID is required');
      if (!this.$v.callbackPartnerId.maxLength) errors.push('Maximum length is 36 characters');
      if (!this.$v.callbackPartnerId.noSpace) errors.push('Spaces are not allowed');
      if (!this.$v.callbackPartnerId.alphaNum) errors.push('Only alphanumeric characters are allowed');
      return [...errors, ...this.externalPartnerIdErrors];
    },

    callbackPartnerClientSecretErrors() {
      const errors = [];
      if (!this.$v.callbackPartnerClientSecret.$dirty) return [...errors, ...this.externalClientSecretErrors];
      if (!this.$v.callbackPartnerClientSecret.required) errors.push('Callback Partner Client Secret is required');
      if (!this.$v.callbackPartnerClientSecret.maxLength) errors.push('Maximum length is 36 characters');
      if (!this.$v.callbackPartnerClientSecret.noSpace) errors.push('Spaces are not allowed');
      if (!this.$v.callbackPartnerClientSecret.alphaNum) errors.push('Only alphanumeric characters are allowed');
      return [...errors, ...this.externalClientSecretErrors];
    }
  },

  watch: {
    partnerId(val) {
      this.callbackPartnerId = val;
    },
    partnerClientSecret(val) {
      this.callbackPartnerClientSecret = val;
    },
    callbackPartnerId(val) {
      this.$emit('update:partnerId', val);
    },
    callbackPartnerClientSecret(val) {
      this.$emit('update:partnerClientSecret', val);
    }
  },

  mounted() {
    this.callbackPartnerId = this.partnerId;
    this.callbackPartnerClientSecret = this.partnerClientSecret;
  },

  methods: {
    generateRandomString(length = 10) {
      const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      for (let i = 0; i < length; i += 1) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
      }
      return result;
    },

    generateCallbackPartnerId() {
      this.callbackPartnerId = this.generateRandomString(10);
      this.$v.callbackPartnerId.$touch();
    },

    generateCallbackPartnerClientSecret() {
      this.callbackPartnerClientSecret = this.generateRandomString(10);
      this.$v.callbackPartnerClientSecret.$touch();
    },

    resetCallbackPartnerId() {
      this.callbackPartnerId = '';
      this.$v.callbackPartnerId.$reset();
    },

    resetCallbackPartnerClientSecret() {
      this.callbackPartnerClientSecret = '';
      this.$v.callbackPartnerClientSecret.$reset();
    },

    removeSpaces(fieldName) {
      if (fieldName === 'callbackPartnerId') {
        this.callbackPartnerId = this.callbackPartnerId.replace(/ /g, '');
      } else if (fieldName === 'callbackPartnerClientSecret') {
        this.callbackPartnerClientSecret = this.callbackPartnerClientSecret.replace(/ /g, '');
      }
    },

    touchValidation() {
      this.$v.callbackPartnerId.$touch();
      this.$v.callbackPartnerClientSecret.$touch();
    },

    resetValidation() {
      this.$v.callbackPartnerId.$reset();
      this.$v.callbackPartnerClientSecret.$reset();
    }
  }
};
</script>

<style scoped>
.gap-field {
  gap: 8px;
  align-items: flex-start;
}

.button-align {
  align-self: flex-start !important;
  margin-top: 4px !important;
}
</style>
